import React from 'react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { StatusBadge, RegulatoryFlags } from './AssessmentBadges';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';

export function HazardTable({ results, expandedRows, onToggleRow }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-[#F9FAFB]">
          <TableHead className="w-[30px]"></TableHead>
          <TableHead className="font-semibold text-[#111827]">INCI Name</TableHead>
          <TableHead className="font-semibold text-[#111827]">CAS #</TableHead>
          <TableHead className="font-semibold text-[#111827] text-right">%</TableHead>
          <TableHead className="font-semibold text-[#111827]">Regulatory Status</TableHead>
          <TableHead className="font-semibold text-[#111827]">Mutagenicity</TableHead>
          <TableHead className="font-semibold text-[#111827]">Sensitization</TableHead>
          <TableHead className="font-semibold text-[#111827]">Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.map((result, index) => (
          <React.Fragment key={result.cas_number || result.ingredient_name}>
            <TableRow className="hover:bg-gray-50 cursor-pointer" onClick={() => onToggleRow(index)} data-testid={`assessment-row-${index}`}>
              <TableCell>{expandedRows.has(index) ? <CaretUp size={16} className="text-[#4B5563]" /> : <CaretDown size={16} className="text-[#4B5563]" />}</TableCell>
              <TableCell className="font-medium text-[#111827]">{result.ingredient_name}</TableCell>
              <TableCell className="text-[#4B5563] tabular-nums">{result.cas_number || '-'}</TableCell>
              <TableCell className="text-right tabular-nums">{result.concentration.toFixed(2)}</TableCell>
              <TableCell>
                <span className={`text-sm ${result.regulatory_status === 'No restrictions' ? 'text-[#0E9F6E]' : 'text-[#9B1C1C] font-medium'}`}>
                  {result.regulatory_status || '-'}
                </span>
              </TableCell>
              <TableCell>
                <span className={`text-sm ${result.mutagenicity === 'Negative' ? 'text-[#4B5563]' : 'text-[#9B1C1C] font-semibold'}`}>
                  {result.mutagenicity || '-'}
                </span>
              </TableCell>
              <TableCell>
                <span className={`text-sm ${result.sensitization === 'Not classified' ? 'text-[#4B5563]' : 'text-[#723B13] font-medium'}`}>
                  {result.sensitization || '-'}
                </span>
              </TableCell>
              <TableCell><StatusBadge status={result.risk_level} /></TableCell>
            </TableRow>
            {expandedRows.has(index) && (
              <TableRow className="bg-[#F9FAFB]">
                <TableCell colSpan={8} className="p-4">
                  <HazardExpandedDetails result={result} />
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
}

function HazardExpandedDetails({ result }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div><p className="text-[#4B5563] font-medium">Function</p><p className="text-[#111827]">{result.function || '-'}</p></div>
      <div><p className="text-[#4B5563] font-medium">Irritation</p><p className="text-[#111827]">{result.irritation || '-'}</p></div>
      <div><p className="text-[#4B5563] font-medium">Endocrine Activity</p><p className="text-[#111827]">{result.endocrine_activity || '-'}</p></div>
      <div><p className="text-[#4B5563] font-medium">Interpretation</p><p className="text-[#111827]">{result.interpretation || '-'}</p></div>
      <div className="md:col-span-4"><p className="text-[#4B5563] font-medium mb-1">Regulatory Flags</p><RegulatoryFlags flags={result.regulatory_flags} /></div>
    </div>
  );
}

export function FullQRATable({ results, expandedRows, onToggleRow }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-[#F9FAFB]">
          <TableHead className="w-[30px]"></TableHead>
          <TableHead className="font-semibold text-[#111827]">INCI Name</TableHead>
          <TableHead className="font-semibold text-[#111827]">CAS #</TableHead>
          <TableHead className="font-semibold text-[#111827] text-right">%</TableHead>
          <TableHead className="font-semibold text-[#111827] text-right">NOAEL</TableHead>
          <TableHead className="font-semibold text-[#111827] text-right">SED</TableHead>
          <TableHead className="font-semibold text-[#111827] text-right">MoS</TableHead>
          <TableHead className="font-semibold text-[#111827]">Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {results.map((result, index) => (
          <React.Fragment key={result.cas_number || result.ingredient_name}>
            <TableRow className="hover:bg-gray-50 cursor-pointer" onClick={() => onToggleRow(index)} data-testid={`assessment-row-${index}`}>
              <TableCell>{expandedRows.has(index) ? <CaretUp size={16} className="text-[#4B5563]" /> : <CaretDown size={16} className="text-[#4B5563]" />}</TableCell>
              <TableCell className="font-medium text-[#111827]">{result.ingredient_name}</TableCell>
              <TableCell className="text-[#4B5563] tabular-nums">{result.cas_number || '-'}</TableCell>
              <TableCell className="text-right tabular-nums">{result.concentration.toFixed(2)}</TableCell>
              <TableCell className="text-right tabular-nums">{result.noael ? result.noael.toFixed(1) : <span className="text-[#723B13]">TTC</span>}</TableCell>
              <TableCell className="text-right tabular-nums">{result.sed ? result.sed.toFixed(6) : '-'}</TableCell>
              <TableCell className="text-right tabular-nums font-semibold">{result.mos ? <span className={result.mos >= 100 ? 'text-[#0E9F6E]' : 'text-[#F05252]'}>{result.mos.toFixed(0)}</span> : '-'}</TableCell>
              <TableCell><StatusBadge status={result.risk_level} /></TableCell>
            </TableRow>
            {expandedRows.has(index) && (
              <TableRow className="bg-[#F9FAFB]">
                <TableCell colSpan={8} className="p-4">
                  <FullQRAExpandedDetails result={result} />
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
}

function FullQRAExpandedDetails({ result }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div><p className="text-[#4B5563] font-medium">Function</p><p className="text-[#111827]">{result.function || '-'}</p></div>
      <div><p className="text-[#4B5563] font-medium">Dermal Absorption</p><p className="text-[#111827] tabular-nums">{result.dermal_absorption}%</p></div>
      <div><p className="text-[#4B5563] font-medium">TTC Threshold</p><p className="text-[#111827] tabular-nums">{result.ttc ? `${result.ttc} ug/kg bw/day` : '-'}</p></div>
      <div><p className="text-[#4B5563] font-medium">TTC Exceeded</p><p className={result.ttc_exceeded ? 'text-[#F05252] font-semibold' : 'text-[#111827]'}>{result.ttc_exceeded ? 'Yes' : 'No'}</p></div>
      <div className="md:col-span-4"><p className="text-[#4B5563] font-medium mb-1">Regulatory Flags</p><RegulatoryFlags flags={result.regulatory_flags} /></div>
    </div>
  );
}
